import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { combineLatest, Subscription } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { Downloadproducts } from "../models/downloads";
import { PortalService } from "../services/portal-service.service";
import { Contracts } from "src/app/models/contracts";
import { ShellService } from "../services/shell.service";

type DownloadTab = "latest" | "archived";

interface ProductConfig {
  name: string;
  displayName: string;
  showISOs: boolean;
  showAPI: boolean;
  showUpgrades: boolean;
  showBundles: boolean;
}

@Component({
  standalone: false,
  selector: "app-downloads",
  templateUrl: "./downloads.component.html",
  styleUrls: ["./downloads.component.scss"],
})
export class DownloadsComponent implements OnInit, OnDestroy {
  constructor(
    private portalService: PortalService,
    private route: ActivatedRoute,
    private shellService: ShellService,
  ) { }

  downloads = new Downloadproducts();
  private rawDownloads = new Downloadproducts();
  products: { config: ProductConfig; data: any }[] = [];
  @Input() contract: Contracts;
  loading = true;
  active = 1;
  private routeSubscription?: Subscription;
  private isLoadingDownloads = false;
  private lastProductFetched?: string;

  private readonly PRODUCT_CONFIGS: Record<string, ProductConfig> = {
    TRIAL: { name: "TRIAL", displayName: "TRIAL", showISOs: true, showAPI: false, showUpgrades: false, showBundles: false },
    ABMR: { name: "ABMR", displayName: "ABMR", showISOs: true, showAPI: false, showUpgrades: false, showBundles: false },
    CBMR: { name: "CBMR", displayName: "CBMR", showISOs: true, showAPI: false, showUpgrades: false, showBundles: false },
    CLONEMANAGER: {
      name: "CLONEMANAGER",
      displayName: "CloneManager",
      showISOs: true,
      showAPI: false,
      showUpgrades: false,
      showBundles: false,
    },
    COBMR: { name: "COBMR", displayName: "CoBMR", showISOs: true, showAPI: false, showUpgrades: false, showBundles: false },
    NBMR: { name: "NBMR", displayName: "NBMR", showISOs: true, showAPI: false, showUpgrades: false, showBundles: false },
    PBMR: { name: "PBMR", displayName: "PBMR", showISOs: true, showAPI: false, showUpgrades: false, showBundles: false },
    RBMR: { name: "RBMR", displayName: "RBMR", showISOs: true, showAPI: false, showUpgrades: false, showBundles: false },
    SYSBACK: { name: "SYSBACK", displayName: "SysBack", showISOs: false, showAPI: false, showUpgrades: false, showBundles: false },
    TBMR: { name: "TBMR", displayName: "TBMR", showISOs: true, showAPI: false, showUpgrades: false, showBundles: false },
    VA: { name: "VA", displayName: "Appliance", showISOs: false, showAPI: true, showUpgrades: true, showBundles: true },
    FSBLOCKER: { name: "FSBLOCKER", displayName: "FSBlocker", showISOs: false, showAPI: false, showUpgrades: false, showBundles: false },
  };

  async ngOnInit() {
    this.routeSubscription?.unsubscribe();
    this.routeSubscription = combineLatest([
      this.route.queryParamMap.pipe(
        map((params) => params.get("tab") || "latest"),
        distinctUntilChanged(),
      ),
      this.shellService.selectedContractIdChanges.pipe(distinctUntilChanged()),
    ]).subscribe(async (params) => {
      try {
        const [tab] = params;
        this.active = tab == "archived" ? 2 : 1;
        this.contract = this.shellService.selectedContract;

        if (!this.contract) {
          this.contract = new Contracts();
          this.contract.product = "TRIAL";
          this.contract.active = true;
        }

        if (this.contract) {
          await this.getDownloads();
        } else {
          this.products = [];
        }
      } catch {
        this.products = [];
        this.loading = false;
      }
    });
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  async getDownloads() {
    const currentProduct = this.contract?.product;

    if (!currentProduct) {
      this.downloads = new Downloadproducts();
      this.rawDownloads = new Downloadproducts();
      this.products = [];
      this.loading = false;
      return;
    }

    if (this.isLoadingDownloads) {
      return; // Prevent concurrent calls
    }

    if (this.lastProductFetched === currentProduct && Object.keys(this.rawDownloads || {}).length > 0) {
      this.applyCurrentView();
      return;
    }

    try {
      this.isLoadingDownloads = true;
      this.loading = true;
      this.rawDownloads = await this.portalService.getDownloads(currentProduct);
      this.lastProductFetched = currentProduct;
      this.applyCurrentView();
    } catch {
      this.downloads = new Downloadproducts();
      this.rawDownloads = new Downloadproducts();
      this.products = [];
    } finally {
      this.loading = false;
      this.isLoadingDownloads = false;
    }
  }

  get pageTitle(): string {
    return this.active === 2 ? "Vault" : "Latest Downloads";
  }

  private rebuildProducts(): void {
    const result: { config: ProductConfig; data: any }[] = [];
    for (const [key, config] of Object.entries(this.PRODUCT_CONFIGS)) {
      if (this.shouldShowProduct(key)) {
        const productData = this.downloads[key.toLowerCase()];
        if (productData) {
          const data = this.active === 2 ? productData.archive?.[0] : productData.latest?.[0];
          if (data && this.hasVisibleDownloads(data, config)) {
            result.push({ config, data });
          }
        }
      }
    }
    this.products = result;
  }

  private hasVisibleDownloads(data: any, config: ProductConfig): boolean {
    return (
      !!data?.installs?.length ||
      (config.showISOs && !!data?.isos?.length) ||
      (config.showAPI && !!data?.api?.length) ||
      (config.showUpgrades && !!data?.upgrades?.length) ||
      (config.showBundles && !!data?.bundles?.length) ||
      !!data?.documentation?.length
    );
  }

  private applyCurrentView(): void {
    this.downloads = this.active === 2 ? this.getFilteredArchivedDownloads() : this.rawDownloads;
    this.rebuildProducts();
  }

  private shouldShowProduct(productKey: string): boolean {
    return productKey === "VA" || productKey === "FSBLOCKER" || this.contract?.product === "TRIAL" || this.contract?.product === productKey;
  }

  private filterArchivedDownloads() {
    this.downloads = this.getFilteredArchivedDownloads();
  }

  private getFilteredArchivedDownloads(): Downloadproducts {
    if (!this.contract || this.contract.product === "TRIAL") return this.rawDownloads;

    const filterByDate = (items: any[]): any[] => (items || []).filter((item) => item.lastModified < this.contract.endDate);
    const filteredDownloads = new Downloadproducts();

    for (const [key] of Object.entries(this.PRODUCT_CONFIGS)) {
      const productKey = key.toLowerCase();
      const sourceProduct = this.rawDownloads[productKey];

      filteredDownloads[productKey] = sourceProduct;

      if (this.shouldShowProduct(key)) {
        const productData = sourceProduct;
        if (productData?.archive?.[0]) {
          const archiveData = productData.archive[0];
          const filteredArchive = {
            ...archiveData,
            installs: filterByDate(archiveData.installs),
            isos: filterByDate(archiveData.isos),
            api: filterByDate(archiveData.api),
            upgrades: filterByDate(archiveData.upgrades),
            bundles: filterByDate(archiveData.bundles),
            documentation: filterByDate(archiveData.documentation),
          };

          filteredDownloads[productKey] = {
            ...productData,
            archive: [{ ...filteredArchive }],
          };
        }
      }
    }

    return filteredDownloads;
  }

  isCloneManager(product: string): boolean {
    return product === "CLONEMANAGER";
  }

  get hasCloneManager(): boolean {
    return this.contract.product === "TRIAL" || this.contract.product === "CLONEMANAGER";
  }

  get showVaultExpiryNotice(): boolean {
    return this.active === 2 && this.products.length === 0 && !!this.contract?.endDate && !["TRIAL", "CLONEMANAGER"].includes(this.contract.product);
  }

  refresh(force = false): void {
    if (force) {
      this.lastProductFetched = undefined;
    }

    void this.getDownloads();
  }
}
